/**
 * Changing the bench: the operations a gesture turns into.
 *
 * Here rather than in the component for the same reason the joining rule is:
 * they are statements about the arrangement, they have edge cases — a part
 * taken away takes its ropes with it — and a jest test can hold them to it.
 * The component is then gestures, layout and a clock, and nothing else.
 *
 * EVERY ONE OF THESE RE-MEASURES THE ROPES. That asymmetry is the whole
 * meaning of "inextensible": moving a part changes the arrangement and how
 * much rope it took, running it does not.
 */
import { canTake, hangPlumb, joinPorts, remeasure, type Snap } from './join';
import { reconcile } from './reconcile';
import { settle, stands } from './settle';
import { buildRun, worldAt } from './timeline';
import { diagnose, isBuildable } from './diagnose';
import { constraintsOf } from './constraints';
import { solve } from './solve';
import { defOf, kindOf } from './registry';
import { Link, Part, PortRef, Rope, Vec2, World } from './types';
import { bodiesOf, bondsOf, extentAlong, freedomOf, isFree, measureSpans, partById, portAt, portsOf, released, segmentsOf, spansOf, supportOf } from './world';

/** Parts snap to this, in metres, so ropes come out vertical without aiming.
 *
 *  0.1 and not 0.2, because a rope leaves a sheave at its rim: a 0.3 m pulley
 *  on a 0.2 m grid has its contact points half a step off it, and nothing the
 *  student dragged could ever line up with them. */
export const GRID = 0.1;
export const snap = (v: number): number => Math.round(v / GRID) * GRID;


/** An id no preset can already be using. */
export function mintId(w: World, kind: string): string {
    let n = 1;
    const taken = (id: string): boolean => w.parts.some(p => p.id === id) || w.links.some(l => l.id === id);
    while (taken(`${kind}-${n}`)) n++;
    return `${kind}-${n}`;
}

/** Where a new part goes: at the height its kind usually lives, in the first
 *  clear space left to right, rather than on top of the last one. */
function freeSpot(w: World, kindKey: string): Vec2 {
    const def = kindOf(kindKey);
    // `home` is how far below the beam the kind usually lives.
    const y = def.onFloor ? w.room.floor : w.room.ceiling - def.home;
    const taken = w.parts.filter(p => Math.abs(p.at.y - y) < 0.5).map(p => p.at.x).sort((a, b) => a - b);
    let x = 1.0;
    while (taken.some(t => Math.abs(t - x) < 0.55) && x < w.room.width - 0.8) x += 0.6;
    return { x: snap(x), y: snap(y) };
}

/**
 * Put a new part on the bench — HELD, if it is the sort of thing that falls.
 *
 * You do not drop a weight on the table and then chase it. A bench is built by
 * standing things where you want them and keeping a finger on whatever would
 * otherwise go, and letting go once the machine holds itself — so a part that
 * could move arrives pinned, and stays exactly where the reader put it.
 *
 * This is a CONVENIENCE, and it is only safe because a pin now means one thing
 * everywhere. It used to be load-bearing: the engine could not answer for an
 * unheld part, so the pin was there to make sure it was never asked, and with
 * everything pinned by default a pin said nothing about what the reader
 * wanted — which is why `reconcile` had to ignore pins to be useful at all.
 * The engine can answer now (a part with nothing holding it falls, and the
 * floor catching it is a real force), so the default is a starting position
 * rather than a load-bearing assumption, and every part of the bench can
 * honour a pin as what it is: THIS ONE STAYS WHERE I PUT IT.
 *
 * A hook or a table needs no pin: nothing was going to move it.
 */
export function place(w: World, kindKey: string): { world: World; id: string } {
    const id = mintId(w, kindKey);
    const made = kindOf(kindKey).create(freeSpot(w, kindKey), id) as Part;
    const next = { ...w, parts: [...w.parts, made] };
    const part = freedomOf(next, made).axes.length ? { ...made, pinned: true } : made;
    const placed = { ...w, parts: [...w.parts, part] };
    /* Something with weight, on a bench that has a table or a wedge, goes ON
     * it — over the surface and down onto its face, lying along it, and held.
     * Put in mid-air beside a surface it only started a question the reader
     * then had to answer with a drag and a menu, when standing it on the
     * surface was the reason there was a surface. Parts with no weight — a
     * pulley — are not stood on anything; they are bolted to it. */
    if (kindOf(kindKey).mass(made as never) > 0 && !kindOf(kindKey).supports) {
        const spot = firstFreeFace(placed, id);
        if (spot) {
            const seat = seatAt(placed, id, spot.x, spot.surfaceId);
            if (seat) {
                return {
                    world: stands({
                        ...placed,
                        parts: placed.parts.map(p => (p.id === id
                            ? { ...p, at: seat.at, support: seat.support, face: seat.face, pinned: true }
                            : p)),
                    }),
                    id,
                };
            }
        }
    }
    return { world: stands(placed), id };
}

/**
 * Put a rope on the bench: two loose ends with a rope between them.
 *
 * A rope is not a part, but a reader looking for one expects to pick it up
 * and put it somewhere. Two rope ends make that literal — each is a point the
 * rope stops at, and joining one to a hook, a block or a rim carries the rope
 * on to it and consumes the end, which is the joining rule already.
 *
 * NOTHING IS SOLVED. Two held ends and the rope between them carry nothing and
 * touch nothing, so the bench already knows the answer: everything else stays
 * exactly where it was. Only the new rope is measured.
 */
export function placeRope(w: World): { world: World; ends: [string, string] } {
    const add = (x: World): { world: World; id: string } => {
        const id = mintId(x, 'ropeEnd');
        const made = kindOf('ropeEnd').create(freeSpot(x, 'ropeEnd'), id) as Part;
        return { world: { ...x, parts: [...x.parts, made] }, id };
    };
    const a = add(w), b = add(a.world);
    const rope: Rope = {
        id: mintId(b.world, 'rope'),
        type: 'rope',
        path: [{ partId: a.id, portId: 'end' }, { partId: b.id, portId: 'end' }],
        length: 0,
    };
    const lengths = measureSpans(b.world, rope);
    const measured = { ...rope, length: lengths.reduce((s, l) => s + l, 0), lengths };
    return { world: { ...b.world, links: [...b.world.links, measured] }, ends: [a.id, b.id] };
}

/**
 * Carry a line's loose end to `at`, the line exactly as long as that needs.
 *
 * What dragging a rope or rod end MEANS: the end goes where the hand is, and
 * the rope is neither left slack nor stretched — it is re-measured to its path,
 * so a rope stays taut and a rod is the length of the gap. Nothing is solved:
 * it is cheap enough for every pointer move, and the bench is asked once, when
 * the end is let go. Only a point that snaps as a rope is carried this way.
 */
export function pullEndTo(w: World, id: string, at: Vec2): World {
    const part = partById(w, id);
    if (!part || !portsOf(w, part).some(p => p.joins.snaps === 'rope')) return w;
    const to = {
        x: Math.max(0, Math.min(w.room.width, at.x)),
        y: Math.max(w.room.floor, Math.min(w.room.ceiling, at.y)),
    };
    const moved = { ...w, parts: w.parts.map(p => (p.id === id ? { ...p, at: to } : p)) };
    return {
        ...moved,
        links: moved.links.map(l => {
            if (l.type !== 'rope' || !l.path.some(s => s.partId === id)) return l;
            const lengths = measureSpans(moved, l);
            return { ...l, length: lengths.reduce((s, x) => s + x, 0), lengths };
        }),
    };
}

/**
 * Where a part stands if it is on a surface directly at this x.
 *
 * x ONLY, because that is the one number a reader means when they slide a
 * block along a slope: how far across. Where it is up and down is the
 * surface's answer, not theirs — asking them for both let a drag hold a block
 * off the face and then snap it back somewhere else.
 *
 * Of every face under x, the HIGHEST, since that is the one something lowered
 * from above meets first. An upright face is skipped: x says nothing about
 * where along a vertical side something is. Past the ends of every face it
 * clamps to the nearest place it can still stand, so a drag off a table stops
 * at its edge rather than leaving the table. `onlyOn` keeps it to one surface.
 */
export function seatAt(w: World, id: string, x: number, onlyOn?: string):
    { support: string; face?: string; at: Vec2 } | undefined {
    const part = partById(w, id);
    if (!part) return undefined;
    let best: { support: string; face?: string; at: Vec2 } | undefined;
    let nearest: { support: string; face?: string; at: Vec2; miss: number } | undefined;
    for (const f of surfacesFor(w, id)) {
        if (onlyOn && f.part.id !== onlyOn) continue;
        const sup = defOf(f.part).supports?.(f.part as never, w, f.face);
        if (!sup) continue;
        const across = sup.axes[0].x;
        if (Math.abs(across) < 1e-6) continue;
        // Measured as it WILL lie on this face — see `rest`.
        const on: Part = { ...part, support: f.part.id, face: f.face };
        const clear = extentAlong(w, on, { x: -sup.axes[1].x, y: -sup.axes[1].y });
        const room = Math.max(0, sup.half - extentAlong(w, on, sup.axes[0]));
        const want = (x - sup.seat(0, clear).x) / across;
        const s = Math.max(-room, Math.min(room, want));
        const at = sup.seat(s, clear);
        const miss = Math.abs(want - s) * Math.abs(across);
        if (miss < 1e-9) {
            if (!best || at.y > best.at.y) best = { support: f.part.id, face: f.face, at };
        } else if (!nearest || miss < nearest.miss) {
            nearest = { support: f.part.id, face: f.face, at, miss };
        }
    }
    if (best) return best;
    return nearest && { support: nearest.support, face: nearest.face, at: nearest.at };
}

/**
 * Make a join point on a surface at this x — anywhere along its top — and say
 * which point it is. Undefined for a part that has no such thing.
 */
export function mountAt(w: World, partId: string, x: number): { world: World; ref: PortRef } | undefined {
    const part = partById(w, partId);
    const make = part && defOf(part).mountAt;
    if (!part || !make) return undefined;
    const { part: next, portId } = make(part as never, x);
    return {
        world: { ...w, parts: w.parts.map(p => (p.id === partId ? next as Part : p)) },
        ref: { partId, portId },
    };
}

/** How far apart a bolt's two points are. */
export function boltGap(w: World, bondId: string): number {
    const b = bondsOf(w).find(l => l.id === bondId);
    const p = b && portAt(w, b.hangs)?.at, q = b && portAt(w, b.holds)?.at;
    return p && q ? Math.hypot(p.x - q.x, p.y - q.y) : 0;
}

/**
 * What a bolt to a surface does to the part fastened there — whatever that
 * part's kind says in `onFace`. Anything else a bolt joins is left as it was.
 *
 * The bolt is still a bolt: its gap can be opened again afterwards.
 */
export function seatBolt(w: World, bondId: string): World {
    const b = bondsOf(w).find(l => l.id === bondId);
    if (!b) return w;
    const ends = [b.hangs, b.holds].map(r => partById(w, r.partId));
    const face = ends.find(p => p && defOf(p).supports);
    const seated = ends.find(p => p && p !== face && defOf(p).onFace);
    const rule = seated && defOf(seated).onFace?.(seated as never);
    if (!face || !seated || !rule) return w;
    const sized = setData(w, seated.id, rule.data as Record<string, unknown>);
    return rule.flush ? setBoltGap(sized, bondId, 0) : sized;
}

/**
 * Is joining these two points FASTENING a part to a surface?
 *
 * A point that bolts when dropped (`snaps: 'bond'`), on a part that says what
 * it becomes on a face (`onFace`), joined to a point on a surface. Then the
 * join is a bolt whichever was picked first — made as a rope it hung the pulley
 * on a line from the apex. Returned as [holds, hangs]: the surface first, so the
 * fastened part is the side that hangs; a bolt recorded backwards draws no strap.
 */
export function fastening(w: World, a: PortRef, b: PortRef): [PortRef, PortRef] | undefined {
    const fastens = (r: PortRef): boolean => {
        const p = partById(w, r.partId);
        return !!p && !!defOf(p).onFace && portAt(w, r)?.joins.snaps === 'bond';
    };
    const face = (r: PortRef): boolean => {
        const p = partById(w, r.partId);
        return !!p && !!defOf(p).supports;
    };
    if (fastens(a) && face(b)) return [b, a];
    if (fastens(b) && face(a)) return [a, b];
    return undefined;
}

/** Bolt `hangs` to `holds`, and seat it: the join, retied as a bolt. A point
 *  that cannot be bolted leaves the rope the join made. */
export function fasten(w: World, holds: PortRef, hangs: PortRef, mint: (kind: string) => string): World {
    const joined = joinPorts(w, holds, hangs, mint);
    const added = joined.links.find(l => !w.links.some(o => o.id === l.id));
    return added ? seatBolt(retie(joined, added.id, 'bond'), added.id) : joined;
}

/**
 * Close a bolt up, or open it out: slide the loose side along the line
 * between its two points until they are `gap` apart.
 *
 * A bolt has no length of its own — it is an identity, and holds whatever
 * places its parts were in (see `Bond`). So this is an EDIT of where things
 * are, not a property of the bolt: the side that is not structure moves, and
 * everything bolted to it moves too, the same as a drag. At zero the two
 * points coincide — a pulley's axle on the surface it is fastened to.
 */
export function setBoltGap(w: World, bondId: string, gap: number): World {
    const b = bondsOf(w).find(l => l.id === bondId);
    if (!b) return w;
    const structure = (ref: PortRef): boolean => {
        const p = partById(w, ref.partId);
        return !!p && freedomOf(w, { ...p, pinned: undefined }).axes.length === 0;
    };
    const fixed = structure(b.holds) || !structure(b.hangs) ? b.holds : b.hangs;
    const loose = fixed === b.holds ? b.hangs : b.holds;
    const from = portAt(w, fixed)?.at, to = portAt(w, loose)?.at;
    if (!from || !to) return w;
    const d = Math.hypot(to.x - from.x, to.y - from.y);
    // Already flush, there is no line to open out along: open it upward.
    const dir = d > 1e-9 ? { x: (to.x - from.x) / d, y: (to.y - from.y) / d } : { x: 0, y: 1 };
    const shift = { x: from.x + dir.x * Math.max(0, gap) - to.x, y: from.y + dir.y * Math.max(0, gap) - to.y };
    // What moves with the loose side is what is bolted to IT — not, through
    // this bolt, the structure it is being brought up to.
    const moving = rigidWith({ ...w, links: w.links.filter(l => l.id !== bondId) }, loose.partId);
    return stands({
        ...w,
        parts: w.parts.map(p => (moving.has(p.id) ? { ...p, at: { x: p.at.x + shift.x, y: p.at.y + shift.y } } : p)),
    });
}

/** Where a new part first stands: the middle of the first face with nothing
 *  already standing near it, or the first face's middle if every one is taken. */
function firstFreeFace(w: World, id: string): { surfaceId: string; x: number } | undefined {
    let first: { surfaceId: string; x: number } | undefined;
    for (const f of surfacesFor(w, id)) {
        const sup = defOf(f.part).supports?.(f.part as never, w, f.face);
        if (!sup || Math.abs(sup.axes[0].x) < 1e-6) continue;
        const x = sup.seat(0, 0).x;
        const spot = { surfaceId: f.part.id, x };
        if (!first) first = spot;
        const crowded = w.parts.some(p => p.id !== id && p.support === f.part.id
            && (p.face ?? undefined) === f.face && Math.abs(p.at.x - x) < 0.5);
        if (!crowded) return spot;
    }
    return first;
}

/** Take a part off the bench. A rope through a part that is gone is not a
 *  rope, so it goes too — rather than leaving a dangling path for `diagnose`
 *  to complain about forever. */
export function removePart(w: World, id: string): World {
    return stands(tidy({
        ...w,
        parts: w.parts.filter(p => p.id !== id),
        links: w.links.filter(l => (l.type === 'rope'
            ? !l.path.some(s => s.partId === id)
            : l.hangs.partId !== id && l.holds.partId !== id)),
    }));
}

/**
 * Change WHAT KIND of join this is, between the same two points.
 *
 * There are three, and the difference between them is the whole of what a
 * reader is choosing:
 *
 *   rope   may pull, never push — so what hangs below it hangs, and it may go
 *          slack;
 *   rod    pulls and pushes — so it holds two things APART, and its force is a
 *          number you can read, negative when it is in compression;
 *   bolt   not a force at all: the two parts become one body, no gap, no
 *          length to set and nothing to read.
 *
 * Joining two tie-off points makes a ROPE, which is what a reader means most
 * of the time and the one of the three that fails safely. A pulley hung from a
 * hook by a bracket, or a weight held out on a rigid rod, is one click from
 * there. The join already knows which two points it is between, so changing
 * its kind takes no new information, only saying so.
 */
export function retie(w: World, id: string, to: 'rope' | 'rod' | 'bond'): World {
    const link = w.links.find(l => l.id === id);
    if (!link) return w;
    const ends: [PortRef, PortRef] | null = link.type === 'bond'
        ? [link.holds, link.hangs]
        : link.path.length === 2 ? [link.path[0], link.path[1]] : null;
    // A line threaded over something has more than two ends, and neither a rod
    // nor a bolt is a thing you thread.
    if (!ends) return w;
    // Both points must take the new kind; this link is already counted there.
    if (!ends.every(r => canTake(w, r, to, id))) return w;

    if (to === 'bond') {
        const [a, b] = ends;
        if (![a, b].every(r => {
            const p = partById(w, r.partId);
            return p && defOf(p).bondable;
        })) return w;
        const bond: Link = { id: link.id, type: 'bond', hangs: b, holds: a };
        return stands({ ...w, links: w.links.map(l => (l.id === id ? bond : l)) });
    }

    /* However much rope there WAS, there still is.
     *
     * Rebuilt with `length: 0` this asked to be measured again, and a rope is
     * then however long the run it currently spans — so changing a line's kind
     * and changing it back silently pulled every bit of slack out of it. The
     * two ends have not moved and neither has the rope; only what it is has.
     * A bolt has no length to carry over, so one made from a rope starts from
     * the run it spans, which is the only number there is. */
    const kept = link.type === 'rope' ? link.length : 0;
    const line: Link = {
        id: link.id,
        type: 'rope',
        path: ends,
        length: kept,
        ...(kept > 0 && link.type === 'rope' && link.lengths ? { lengths: link.lengths } : {}),
        ...(to === 'rod' ? { rigid: true } : {}),
        ...(link.type === 'rope' && link.label ? { label: link.label } : {}),
    };
    return stands({ ...w, links: w.links.map(l => (l.id === id ? line : l)) });
}

/**
 * Where one END of a rope or rod could be tied instead — what dragging that end
 * of a selected line lights up.
 *
 * Only a tie-off point (an anchorage), with room for one more of this kind of
 * join once this line has let go of where it is; not a part the line already
 * runs to, which would tie it back onto itself; and not a rope's own loose
 * end, which is something being placed rather than somewhere to place it.
 */
export function ropeEndTargets(w: World, ropeId: string, atEnd: boolean): Snap[] {
    const rope = w.links.find(l => l.id === ropeId);
    if (!rope || rope.type !== 'rope' || rope.path.length < 2) return [];
    const from = atEnd ? rope.path[rope.path.length - 1] : rope.path[0];
    const on = new Set(rope.path.map(s => s.partId));
    const kind = rope.rigid ? 'rod' : 'rope';
    const out: Snap[] = [];
    for (const part of w.parts) {
        if (on.has(part.id)) continue;
        for (const port of portsOf(w, part)) {
            if (port.role !== 'anchorage' || port.joins.snaps) continue;
            const to = { partId: part.id, portId: port.id };
            if (canTake(w, to, kind, ropeId)) out.push({ from, to, kind, at: port.at });
        }
    }
    return out;
}

/**
 * Tie one end of a rope or rod somewhere else.
 *
 * The line is re-rigged, so how much of it there is is asked again — measured
 * taut where it now runs, as a line newly threaded is. Keeping the old length
 * would leave a rope moved from a hook to a far corner either impossibly short
 * or hanging in a loop nobody drew. A target `ropeEndTargets` would not offer
 * leaves the world as it was.
 */
export function moveRopeEnd(w: World, ropeId: string, atEnd: boolean, to: PortRef): World {
    const ok = ropeEndTargets(w, ropeId, atEnd).some(s => s.to.partId === to.partId && s.to.portId === to.portId);
    if (!ok) return w;
    return stands(hangPlumb(tidy({
        ...w,
        links: w.links.map(l => {
            if (l.id !== ropeId || l.type !== 'rope') return l;
            const path = atEnd ? [...l.path.slice(0, -1), to] : [to, ...l.path.slice(1)];
            const { lengths: _spans, ...rest } = l;
            void _spans;
            return { ...rest, path, length: 0 };
        }),
    }), [ropeId]));
}

/** Undo one join: the rope comes off, or the bolt does. The parts stay. */
export const removeLink = (w: World, id: string): World =>
    stands(tidy({ ...w, links: w.links.filter(l => l.id !== id) }));

/**
 * Drop the join points nobody is joined to any more.
 *
 * A point made by pressing along a wedge exists for the join that made it.
 * Left behind when that join is undone, it is a dot on the wedge that means
 * nothing and a port every later join has to look past.
 */
function tidy(w: World): World {
    const used = new Map<string, Set<string>>();
    w.links.forEach(l => (l.type === 'rope' ? l.path : [l.hangs, l.holds]).forEach(s => {
        const set = used.get(s.partId) ?? new Set<string>();
        set.add(s.portId);
        used.set(s.partId, set);
    }));
    return {
        ...w,
        parts: w.parts.map(p => {
            const clean = defOf(p).tidy;
            return clean ? clean(p as never, used.get(p.id) ?? new Set()) as Part : p;
        }),
    };
}

/** Every bond holding a part, so the surface can offer to undo them. */
export const bondsHolding = (w: World, partId: string): string[] =>
    w.links.filter(l => l.type === 'bond' && l.hangs.partId === partId).map(l => l.id);

/**
 * Move a part, within the room.
 *
 * An anchor belongs on the beam; a part resting on something slides along the
 * face it rests on and nowhere else, because that is what resting on it means;
 * everything else lives between the beam and the floor, far enough from each
 * that its own body is still in the room — which is `extent`, the same
 * half-height the floor constraint uses, so a part cannot be dragged somewhere
 * the solver would immediately push it out of.
 *
 * ## A drag does NOT stand the bench up
 *
 * It used to end in `stands`, and that is wrong now that standing up means
 * solving the statics. While a finger is down the reader is deliberately
 * putting the bench somewhere it does not balance, and re-solving under the
 * finger either fights the gesture or refuses it: hold a pulley on an
 * unbalanced Atwood and there IS no equilibrium, so a solve would have nothing
 * to hand back and the part could never be placed at all.
 *
 * So a drag asks the question a drag can answer — where do the lengths let
 * these parts be — which is `reconcile`, and nothing else. The statics is
 * solved once, when the finger comes off: `stands` on pointer-up.
 */
/**
 * Is this a part whose ropes are pulled taut wherever it is dropped?
 *
 * A BLOCK: something with a mass, that no rope wraps round and nothing stands
 * on. A reader dragging a block is placing the load, and the rope tied to it
 * is whatever reaches — not a fixed amount the block has to fight for. A hook,
 * a pulley or a table keeps its ropes as long as they are; see `Rope.length`.
 */
export function tiesTaut(w: World, part: Part): boolean {
    const def = defOf(part);
    return def.mass(part as never) > 0 && !def.supports
        && !portsOf(w, part).some(q => def.ropeContact(part as never, q, part.at).wrapRadius);
}

/** Every rope with an end on one of these parts, measured again where it now
 *  lies — so it is exactly as long as the run, neither slack nor stretched. */
function pullTaut(w: World, ends: Set<string>): World {
    const tied = w.links.some(l => l.type === 'rope'
        && (ends.has(l.path[0]?.partId) || ends.has(l.path[l.path.length - 1]?.partId)));
    if (!tied) return w;
    return remeasure({
        ...w,
        links: w.links.map(l => (l.type === 'rope'
            && (ends.has(l.path[0]?.partId) || ends.has(l.path[l.path.length - 1]?.partId))
            ? { ...l, length: 0, lengths: undefined }
            : l)),
    });
}

export function movePart(w0: World, id: string, to: Vec2): World {
    /* Measure anything unmeasured BEFORE moving, never during.
     *
     * A drag tries several positions before it settles on one, and each try
     * re-seats and re-measures. A rope that had not been measured yet would
     * therefore be born at whichever trial position happened to be running —
     * so how long it came out would depend on how far the drag had got, which
     * is not a length at all. Threading happens where the rope was threaded. */
    const w = remeasure(w0);
    const part = partById(w, id);
    if (!part) return w;
    const half = kindOf(part.kind).extent(part as never);
    // A block's ropes follow it; anything else's ropes keep their length.
    const taut = tiesTaut(w, part);
    const tie = (x: World, ends: Set<string>): World => (taut ? pullTaut(x, ends) : x);

    const on = supportOf(w, part);
    if (on && part.support) {
        /* The drag's x, and the SURFACE's y. It used to project the pointer
         * onto the face it was already on, so a block could not be walked
         * over a wedge's apex onto the other slope, and how far it went
         * depended on how high the pointer was held. Only x is the reader's;
         * which face and how high are what the surface says is under it. */
        const seat = seatAt(w, id, to.x, part.support);
        if (seat) {
            return tie(remeasure({
                ...w,
                parts: w.parts.map(p => (p.id === id ? { ...p, at: seat.at, face: seat.face } : p)),
            }), new Set([id]));
        }
        const clear = extentAlong(w, part, { x: -on.axes[1].x, y: -on.axes[1].y });
        const room = Math.max(0, on.half - extentAlong(w, part, on.axes[0]));
        const s = Math.max(-room, Math.min(room, on.along(to)));
        return tie(remeasure({ ...w, parts: w.parts.map(p => (p.id === id ? { ...p, at: on.seat(s, clear) } : p)) }), new Set([id]));
    }

    const def = kindOf(part.kind);
    const x = Math.max(0.3, Math.min(w.room.width - 0.3, snap(to.x)));
    /* Some things belong to a line and stay on it however far off it they are
     * dragged: a hook to the beam, a table to the floor. */
    const y = def.onBeam ? part.at.y
        : def.onFloor ? w.room.floor
        : Math.max(w.room.floor + half.y, Math.min(w.room.ceiling - half.y, snap(to.y)));

    /* Parts bolted together are ONE thing, so dragging one drags the rest.
     *
     * Not doing so let a pulley be pulled out from under the hook it is bolted
     * to, leaving a strap that ran up to the beam and then sideways along it —
     * the drawing being honest about two positions that the physics had
     * already welded into one body. Each part still obeys its own rule as it
     * goes, which is what keeps a hook on the beam while the pulley it holds
     * moves freely below: the hook takes the sideways half of the move and
     * ignores the rest, and the strap stays upright because both took the
     * same sideways half. */
    const dx = x - part.at.x, dy = y - part.at.y;
    const moving = rigidWith(w, id);
    const moveBy = (f: number): World => remeasure({
        ...w,
        parts: w.parts.map(p => {
            if (!moving.has(p.id)) return p;
            const d = kindOf(p.kind);
            const px = Math.max(0.3, Math.min(w.room.width - 0.3, +(p.at.x + dx * f).toFixed(4)));
            const py = d.onBeam || d.onFloor ? p.at.y : +(p.at.y + dy * f).toFixed(4);
            return { ...p, at: { x: px, y: py } };
        }),
    });

    /* Move it, and let the rest of the bench take up the difference.
     *
     * A rope over a sheave keeps its TOTAL length, so pulling one weight of an
     * Atwood down pays rope out on one side and takes it in on the other — the
     * far weight rises and the rope is untouched. `reconcile` is what works
     * that out; without it the only way to keep a total fixed while one run
     * grows is to move nothing at all, which is what this used to do.
     *
     * When there is genuinely nowhere for the rope to come from — both ends
     * fixed to the beam — `reconcile` says so, and then the honest end of the
     * gesture is the last place that DID work. Bisection rather than
     * arithmetic because a run's length is a sum of distances around wheels,
     * and the answer is wanted to a millimetre, not exactly. */
    const placed = (f: number): World | null => reconcile(tie(moveBy(f), moving), moving);
    const whole = placed(1);
    if (whole) return whole;
    let lo = 0, hi = 1, best: World | null = null;
    for (let i = 0; i < 12; i++) {
        const mid = (lo + hi) / 2;
        const got = placed(mid);
        if (got) { lo = mid; best = got; } else hi = mid;
    }
    // Nowhere to go at all: the arrangement is already against its limit, and
    // the honest answer is that nothing moved.
    return lo > 1e-4 && best ? best : w;
}

/**
 * Everything that has to move when this part does.
 *
 * Bolted parts, because a bond makes one body of them — and parts joined by a
 * ROD, because a rod is rigid and a rigid translation of both its ends is the
 * only move that keeps it its own length. Before lengths were authored a rod
 * simply stretched to suit; now that it cannot, dragging one end has to bring
 * the other, or a rod would be a thing that pins the bench solid.
 */
function rigidWith(w: World, id: string): Set<string> {
    const out = new Set<string>([id]);
    for (let grew = true; grew;) {
        grew = false;
        const reach = (a: string, b: string): void => {
            if (out.has(a) && !out.has(b)) { out.add(b); grew = true; }
        };
        bodiesOf(w).forEach(b => {
            if (b.partIds.some(x => out.has(x))) b.partIds.forEach(x => reach(b.partIds.find(y => out.has(y)) as string, x));
        });
        w.links.forEach(l => {
            if (l.type !== 'rope' || !l.rigid || l.path.length !== 2) return;
            reach(l.path[0].partId, l.path[1].partId);
            reach(l.path[1].partId, l.path[0].partId);
        });
    }
    return out;
}


/**
 * Stand a part on a surface, or take it off one.
 *
 * An EDIT and never a discovery. A block placed on a table stays on it, and
 * what would otherwise have to happen — noticing mid-solve that a body had
 * arrived at a face and changing which ways it may travel while the solver was
 * mid-thought — is real physics, no part of this syllabus, and a great deal of
 * machinery to get subtly wrong. See `types.ts` on `Part.support`.
 */
export function rest(w: World, id: string, surfaceId: string | null, which?: string): World {
    const part = partById(w, id);
    if (!part) return w;
    if (!surfaceId) {
        const off = w.parts.map(p => {
            if (p.id !== id) return p;
            const { support, face, ...bare } = p;
            return bare as Part;
        });
        return stands({ ...w, parts: off });
    }
    const on = partById(w, surfaceId);
    const face = on && defOf(on).supports?.(on as never, w, which);
    if (!face) return w;
    /* Measured as it WILL lie, not as it lies now: until this edit lands it is
     * not on the face, so its own frame is still level and a block about to
     * lie on a slope would be measured standing upright. */
    const placed: Part = { ...part, support: surfaceId, face: which };
    const clear = extentAlong(w, placed, { x: -face.axes[1].x, y: -face.axes[1].y });
    const room = Math.max(0, face.half - extentAlong(w, placed, face.axes[0]));
    const was = face.along(part.at);
    /* Where it was, if that was anywhere on the face — otherwise the middle.
     *
     * Clamping instead parks it hard against the lip, and a block resting
     * exactly on a stop is a row at its limit: two of those plus the rope
     * between them over-determine the very coordinates the rope pulls on, and
     * the bench answers "this does not decide what happens" to an arrangement
     * a reader thought they were still building. Standing something on a face
     * should put it ON the face, not on the edge of it. */
    const s = Math.abs(was) <= room ? was : 0;
    return stands({
        ...w,
        parts: w.parts.map(p => (p.id === id
            ? { ...p, at: face.seat(s, clear), support: surfaceId, face: which }
            : p)),
    });
}

/** Every face of every surface a part could be stood on, named so a reader can
 *  choose between the two slopes of one wedge. */
export const surfacesFor = (w: World, id: string): { part: Part; face?: string; label: string }[] =>
    w.parts.filter(p => p.id !== id && !!defOf(p).supports).flatMap(p => {
        const name = defOf(p).name(p as never, w);
        const faces = defOf(p).faces?.(p as never, w);
        return faces && faces.length > 1
            ? faces.map(f => ({ part: p, face: f.id, label: `${name} — ${f.label}` }))
            : [{ part: p, label: name }];
    });

/**
 * Hold a part where it is, or let it go.
 *
 * Its own operation and not a `data` field, because every kind of part can be
 * held and none of them has to know what being held means.
 *
 * LETTING GO SETTLES THE BENCH, and that is the whole of why this is worth
 * having. A hanging body has one direction of travel — straight down — so the
 * engine cannot swing anything: drag a pinned weight sideways and it sits
 * there with its rope leaning and a sideways force nothing balances. The swing
 * is not something we can simulate, but where it ENDS is not in doubt, so
 * releasing the pin puts it there. The same step takes up slack and puts a
 * weight with nothing under it on the floor.
 */
/**
 * Take out every pin that is holding nothing up.
 *
 * A pin is a hand keeping something still while the bench is built. Once the
 * machine holds that part itself — a pulley bolted to a wedge, a hook's load
 * tied between two hooks — the hand is doing nothing, and a pin left in says
 * the part is held when it is not what holds it. Asked of the bench with every
 * pin out, the same question the Pin button asks. Nothing moves: a part that
 * would stand still anyway stands still.
 */
export function dropIdlePins(w: World): World {
    if (!w.parts.some(p => p.pinned)) return w;
    const free = new Set(bodiesOf(released(w)).filter(isFree).flatMap(b => b.partIds));
    if (!w.parts.some(p => p.pinned && !free.has(p.id))) return w;
    return {
        ...w,
        parts: w.parts.map(p => {
            if (!p.pinned || free.has(p.id)) return p;
            const { pinned: _held, ...rest } = p;
            return rest as Part;
        }),
    };
}

export function pin(w: World, id: string, held: boolean): World {
    const next = remeasure({
        ...w,
        parts: w.parts.map(p => (p.id === id ? { ...p, pinned: held || undefined } : p)),
    });
    return held ? next : settle(next);
}



/**
 * Set how much rope there is — winding it in as readily as paying it out.
 *
 * The length is the INPUT and the positions follow, which is the whole of what
 * `reconcile` is for. Shortening a rope makes it too short for the run it
 * spans, so reconcile pulls the ends together until it fits — and that is a
 * winch, worked out from the constraint rows rather than guessed at.
 *
 * It used to guess. `haulingEnd` picked whichever end "had most purchase" and
 * slid it along the run, then wrote the asked-for length down whether or not
 * the slide had achieved it. While `remeasure` still re-measured every rope on
 * every edit that was invisible: the length was quietly rewritten to whatever
 * the geometry had become. With the length authored it stopped being
 * invisible, and what it had been doing all along came out — winding a
 * one-pulley bench in to 1 m left a rope 1.0 m long spanning a run of 2.7 m,
 * and paying it back out left 0.89 m of slack that nothing would ever take up,
 * on a bench reporting no fault at all.
 */
export function setRopeLength(w0: World, ropeId: string, want: number): World {
    const w = remeasure(w0);
    const rope = w.links.find(l => l.id === ropeId);
    if (!rope || rope.type !== 'rope') return w;

    const now = spansOf(w).filter(s => s.ropeId === ropeId).reduce((a, s) => a + s.now, 0);
    /* Never longer than the bench could ever use, however the caller asks. A
     * length is a number from a slider and a slider is a thing that can be
     * given a bad number; a rope of 10²² metres takes every other number on the
     * bench down with it. */
    const most = now + (w.room.ceiling - w.room.floor) + w.room.width;
    const want2 = Math.min(most, Math.max(0.05, want));

    // Paying out asks nothing of anybody; `settle` then lets go of the slack.
    if (want2 >= now - 1e-9) return settle(measured(w, ropeId, want2));

    const got = wind(w, ropeId, want2);
    if (got) return settle(got);

    /* As short as it will go, and no shorter. Bisection for the same reason
     * the drag uses it: how far a rope can come in is a question about
     * distances around wheels, and the answer is wanted to a millimetre. */
    let lo = want2, hi = now, best: World | null = null;
    for (let i = 0; i < 14; i++) {
        const mid = (lo + hi) / 2;
        const r = wind(w, ropeId, mid);
        if (r) { hi = mid; best = r; } else lo = mid;
    }
    return best ? settle(best) : w;
}

/** This rope at that length, with the bench moved to suit — or `null` if it
 *  will not come in that far. */
const wind = (w: World, ropeId: string, L: number): World | null =>
    reconcile(measured(w, ropeId, L), new Set());

const measured = (w: World, ropeId: string, L: number): World => ({
    ...w,
    links: w.links.map(l => (l.id === ropeId && l.type === 'rope'
        ? { ...l, length: L, lengths: undefined }
        : l)),
});

/**
 * The shortest this rope can be made — the slider's left stop.
 *
 * Asked by winding it in and seeing where it sticks, rather than estimated
 * from the gaps between parts. A control whose limit is a guess is a control
 * that either refuses somewhere it needn't or promises somewhere it can't.
 */
export function shortestRope(w0: World, ropeId: string): number {
    const w = remeasure(w0);
    const now = spansOf(w).filter(s => s.ropeId === ropeId).reduce((a, s) => a + s.now, 0);
    if (!wind(w, ropeId, now - 1e-3)) return +now.toFixed(3);
    let lo = 0.05, hi = now;
    for (let i = 0; i < 14; i++) {
        const mid = (lo + hi) / 2;
        if (wind(w, ropeId, mid)) hi = mid; else lo = mid;
    }
    return +hi.toFixed(3);
}

/** Is there anything for this rope to haul? A line strung between two points
 *  on the beam has nothing: shortening it would have to move the ceiling. */
export function windable(w0: World, ropeId: string): boolean {
    const w = remeasure(w0);
    const now = spansOf(w).filter(s => s.ropeId === ropeId).reduce((a, s) => a + s.now, 0);
    return !!wind(w, ropeId, now - 1e-3);
}

/**
 * Pay out this much rope beyond what the run needs.
 *
 * A surplus asked for, turned into the length it implies — not stored as one,
 * because with `length` authored the two would be two answers to one question.
 * `slackOf` reads it back.
 */
export function setSlack(w0: World, ropeId: string, slack: number): World {
    const w = remeasure(w0);
    const rope = w.links.find(l => l.id === ropeId);
    // A rod is exactly its length in both directions; spare rod is not a thing.
    if (!rope || rope.type !== 'rope' || rope.rigid) return w;
    const now = spansOf(w).filter(s => s.ropeId === ropeId).reduce((a, s) => a + s.now, 0);
    return setRopeLength(w, ropeId, now + Math.max(0, slack));
}

/** Change what a part is made of — a weight's kilograms, a hand's pull. */
export function setData(w: World, id: string, patch: Record<string, unknown>): World {
    /* A mass is not only a number. It sets how big the weight is drawn, which
     * moves the hook on its top face, which changes the run below it — so
     * changing a mass opens a gap in a rope, and a gap nothing takes up is a
     * load reported as held by rope that is carrying nothing. */
    const next = { ...w, parts: w.parts.map(p => (p.id === id ? { ...p, data: { ...p.data, ...patch } } : p)) };
    return seatWhatIsInside(stands(followBolts(w, next, id)), id);
}

/**
 * A surface reshaped around a block puts the block ON it.
 *
 * Turn a table's side from upright to leaning out and the pulley at its top
 * corner goes with it — but a block hanging from that pulley stayed where it
 * hung, now inside the wood, with its rope through it: a bench that refuses to
 * run, over an arrangement nobody built. The block belongs on the face that
 * came to meet it, which is where a real one would be: resting on the slope,
 * the rope running down it.
 *
 * Inside means behind every face of the surface and above its base. Such a
 * block, free and standing on nothing, is stood on the face it is nearest.
 * Anything else — a block already on a face, one held where it is, one clear
 * of the wood — is left alone.
 */
function seatWhatIsInside(w: World, surfaceId: string): World {
    const surface = partById(w, surfaceId);
    const def = surface && defOf(surface);
    if (!surface || !def?.supports || !def.faces) return w;
    const faces = def.faces(surface as never, w).map(f => ({ id: f.id, at: def.supports?.(surface as never, w, f.id) }));
    let out = w;
    for (const block of w.parts) {
        if (block.kind !== 'weight' || block.support || block.pinned) continue;
        if (block.at.y <= surface.at.y) continue;
        // How far out of each face the centre is; behind every one is inside.
        const past = faces.map(f => {
            if (!f.at) return { id: f.id, d: Infinity };
            const mid = f.at.seat(0, 0);
            return { id: f.id, d: -((block.at.x - mid.x) * f.at.axes[1].x + (block.at.y - mid.y) * f.at.axes[1].y) };
        });
        if (!past.every(f => f.d < 0)) continue;
        const nearest = past.reduce((a, b) => (b.d > a.d ? b : a));
        out = rest(out, block.id, surfaceId, nearest.id);
    }
    return out;
}

/**
 * One of a part's resize handles dragged to `to` — reshaped, and whatever is
 * bolted to its corners carried along, but NOTHING SOLVED: cheap enough for
 * every pointer move, so the drawing can follow the hand.
 */
export function reshape(w: World, id: string, handle: string, to: Vec2): World {
    const part = partById(w, id);
    const r = part && defOf(part).resize?.(part as never, handle, to, w);
    if (!part || !r) return w;
    const next = {
        ...w,
        parts: w.parts.map(p => (p.id === id ? { ...p, ...(r.at ? { at: r.at } : {}), data: { ...p.data, ...(r.data as Record<string, unknown>) } } : p)),
    };
    return followBolts(w, next, id);
}

/** The same, kept: and the bench asked, once, where everything now stands. */
export function resizePart(w: World, id: string, handle: string, to: Vec2): World {
    const next = reshape(w, id, handle, to);
    return next === w ? w : seatWhatIsInside(stands(next), id);
}

/**
 * What is bolted to a POINT of a part goes where that point goes.
 *
 * A bolt records which point it is fastened to — a wedge's apex, a table's
 * corner, a point made along a slope — and the solve then holds the two
 * parts' CENTRES at the offset they had. That is right while nothing about the
 * part's shape changes, and wrong the moment it does: a wedge's centre is the
 * middle of its base and stays put when its angles change, so a pulley bolted
 * to the apex stayed put too, and the apex left without it. So when a part's
 * data changes, every bolted point of it is measured before and after, and
 * whatever is bolted there — with everything bolted to THAT — moves by exactly
 * as much as its point did.
 */
function followBolts(before: World, after: World, id: string): World {
    let out = after;
    bondsOf(before).forEach(b => {
        const mine = b.hangs.partId === id ? b.hangs : b.holds.partId === id ? b.holds : undefined;
        if (!mine) return;
        const other = mine === b.hangs ? b.holds : b.hangs;
        const was = portAt(before, mine)?.at, now = portAt(after, mine)?.at;
        if (!was || !now) return;
        const d = { x: now.x - was.x, y: now.y - was.y };
        if (Math.hypot(d.x, d.y) < 1e-12) return;
        // What travels with the far end — not, through its bolts, the part being edited.
        const apart = { ...out, links: out.links.filter(l => l.type !== 'bond' || (l.hangs.partId !== id && l.holds.partId !== id)) };
        const moving = rigidWith(apart, other.partId);
        moving.delete(id);
        out = { ...out, parts: out.parts.map(p => (moving.has(p.id) ? { ...p, at: { x: p.at.x + d.x, y: p.at.y + d.y } } : p)) };
    });
    return out;
}
